/*
 * Klaviyo integration  ·  assets/hoh-klaviyo.js
 *
 * Why this lives here
 * -------------------
 * Two reasons:
 *   1. Frees ~1.8 KB from sections/hall-of-heroes.liquid (256 KB cap).
 *   2. Lets us implement honest dupe-prevention logic in one place.
 *
 * The dupe problem (and the fix)
 * ------------------------------
 * Klaviyo's create-or-update endpoint upserts by email. Send the same
 * email twice and the existing profile is updated, not duplicated.
 *
 * BUT — the SMS subscription endpoint accepts a phone-only payload
 * (no email). When the user opts into SMS on a later submission, the
 * subscription call sent JUST the phone number, so Klaviyo couldn't
 * match it to the existing email-keyed profile and created a NEW
 * profile keyed by phone. That's the dupe source.
 *
 * The fix: every SMS-related call now includes BOTH email + phone
 * so Klaviyo merges into the existing profile.
 *
 * Per-submission policy (matches user's stated intent)
 * ---------------------------------------------------
 *   First submission for an email:
 *     - Full sync: profile (email + phone if SMS) + character props +
 *       email list subscription + SMS list subscription if opted in.
 *   Subsequent submission for the same email:
 *     - SKIP character-prop updates (the first hero's name stays).
 *     - If they newly opted into SMS this time: update profile to add
 *       phone + subscribe to SMS list (both calls include email).
 *     - If they didn't opt into SMS: do nothing. Profile already exists.
 *
 * "First vs subsequent" is determined by counting how many existing
 * characters in the public list (allCharacters via window.hohGetAllCharacters)
 * share this email. After insert, count==1 means first; count>1 means
 * subsequent. Works across devices because allCharacters is the
 * server-loaded public list.
 *
 * Edits never call Klaviyo (already the case in the section's submit
 * handler).
 *
 * Public:
 *   window.syncHohLeadToKlaviyo(row, phone, smsOptin)
 */
(function(){
  'use strict';

  var COMPANY_ID = 'UeMxA4';
  var LIST_ID = 'TQmrgu';
  var REVISION = '2025-07-15';

  function postJson(url, payload){
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': REVISION },
      body: JSON.stringify(payload)
    }).then(function(r){
      if(r.status >= 200 && r.status < 300) return { success: true };
      return r.text().then(function(t){
        console.error('[Klaviyo] ' + url + ' failed', r.status, t);
        return { success: false, error: t };
      });
    }).catch(function(e){
      console.error('[Klaviyo] fetch error:', e);
      return { success: false, error: e.message };
    });
  }

  /* Profile upsert. Includes email so Klaviyo merges by email. */
  function updateProfile(email, firstName, props, phone, smsConsent){
    if(!email && !phone) return Promise.resolve({ success: false });
    var attributes = {};
    if(email) attributes.email = email;
    if(phone) attributes.phone_number = phone;
    if(firstName) attributes.first_name = firstName;
    if(props && Object.keys(props).length > 0) attributes.properties = props;
    if(smsConsent && phone){
      attributes.subscriptions = { sms: { marketing: { consent: 'SUBSCRIBED' } } };
    }
    return postJson(
      'https://a.klaviyo.com/client/profiles/?company_id=' + COMPANY_ID,
      { data: { type: 'profile', attributes: attributes } }
    );
  }

  /* Email list subscription. Klaviyo keys by email — safe upsert. */
  function subEmail(email, firstName){
    if(!email) return Promise.resolve({ success: false });
    var profileAttrs = { email: email };
    if(firstName) profileAttrs.first_name = firstName;
    return postJson(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + COMPANY_ID,
      {
        data: {
          type: 'subscription',
          attributes: { custom_source: 'Hall of Heroes', profile: { data: { type: 'profile', attributes: profileAttrs } } },
          relationships: { list: { data: { type: 'list', id: LIST_ID } } }
        }
      }
    );
  }

  /* SMS list subscription. CRITICAL: includes email when available so
     Klaviyo merges into the existing email-keyed profile instead of
     creating a phone-only dupe. */
  function subSms(phone, firstName, email){
    if(!phone) return Promise.resolve({ success: true });
    var profileAttrs = {
      phone_number: phone,
      subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
    };
    if(email) profileAttrs.email = email;
    if(firstName) profileAttrs.first_name = firstName;
    return postJson(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + COMPANY_ID,
      {
        data: {
          type: 'subscription',
          attributes: { custom_source: 'Hall of Heroes', profile: { data: { type: 'profile', attributes: profileAttrs } } },
          relationships: { list: { data: { type: 'list', id: LIST_ID } } }
        }
      }
    );
  }

  /* Count existing characters in the public list that share this email
     (auth_email match). Pulled from window.hohGetAllCharacters which the
     section exposes after data load. The just-inserted character is
     already in this list at sync time. */
  function existingCharCount(normEmail){
    if(typeof window.hohGetAllCharacters !== 'function') return 0;
    try {
      var all = window.hohGetAllCharacters() || [];
      var n = 0;
      for(var i = 0; i < all.length; i++){
        var c = all[i];
        if(!c) continue;
        var ae = c.auth_email || c.email;
        if(ae && String(ae).toLowerCase() === normEmail) n++;
      }
      return n;
    } catch(e){ return 0; }
  }

  /* Public: called by the section's submit handler after a successful
     character insert. row.email is required. phone + smsOptin are
     optional (SMS opt-in only). */
  window.syncHohLeadToKlaviyo = function(row, phone, smsOptin){
    if(!row || !row.email) return;
    /* Test-account bypass: when the developer's test submissions go
       through, skip Klaviyo sync so they don't pollute the real
       profile list. Silent skip — no console log so the special-case
       isn't observable from the page. */
    if(row.player_name && String(row.player_name).trim().toLowerCase() === 'jangela'){
      return;
    }
    var normEmail = String(row.email).trim().toLowerCase();
    var normPhone = '';
    try {
      if(phone && typeof window.hohNormalizePhone === 'function'){
        normPhone = window.hohNormalizePhone(phone);
      } else if(phone){
        normPhone = String(phone).replace(/[^\d+]/g, '');
      }
    } catch(e){}
    var firstName = row.player_name || '';

    /* First submission OR subsequent? Server-truth via allCharacters. */
    var count = existingCharCount(normEmail);
    var isFirst = count <= 1;

    if(isFirst){
      /* Full sync: character props go to the profile, both list
         subscriptions fire with email so phone gets merged. */
      var props = {};
      if(row.name) props.HoH_Character_Name = row.name;
      /* R795b (Jan, 2026-09-23: "yes"): once the hero page exists, Klaviyo's
         hero link is the hero's own page; until then, and for a hero the Hall
         does not show, it stays the Hall link. */
      if(row.id){
        var heroPage = (typeof window.hohHeroPageUrl === 'function') ? window.hohHeroPageUrl(row) : '';
        if(heroPage) props.HoH_Character_URL = heroPage;
        else if(typeof window.heroUrl === 'function') props.HoH_Character_URL = window.heroUrl(row.id);
      }
      if(row.race) props.HoH_Character_Race = row.race;
      if(row.character_class) props.HoH_Character_Class = row.character_class;
      if(row.level) props.HoH_Character_Level = row.level;
      if(row.campaign) props.HoH_Character_Campaign = row.campaign;
      if(row.image_url) props.HoH_Character_Image = row.image_url;
      props.HoH_Is_FF_Portrait = row.is_ff_portrait ? 'Yes' : 'No';
      props.HoH_Submission_Source = 'Hall of Heroes';
      props.HoH_Submitted_At = new Date().toISOString();
      if(smsOptin && normPhone) props.HoH_SMS_Optin = 'Yes';

      updateProfile(normEmail, firstName, props, normPhone, smsOptin)
        .then(function(){ return subEmail(normEmail, firstName); })
        .then(function(){
          if(smsOptin && normPhone) return subSms(normPhone, firstName, normEmail);
        })
        .catch(function(e){ console.error('[Klaviyo] first sync', e); });
    } else if(smsOptin && normPhone){
      /* Subsequent submission AND the user newly opted into SMS this
         time. Add phone to existing profile (no character props) +
         subscribe to SMS list. Both calls include email so Klaviyo
         merges into the existing email-keyed profile. */
      updateProfile(normEmail, '', null, normPhone, true)
        .then(function(){ return subSms(normPhone, '', normEmail); })
        .catch(function(e){ console.error('[Klaviyo] phone update', e); });
    }
    /* Subsequent submission with NO new SMS info: do nothing. The
       Klaviyo profile already exists with the user's email + first
       hero's properties. We don't overwrite character_name etc. */
  };
})();
